"use client";

import React, { useState } from "react";
import { MapPin, Mail, Phone, ArrowRight, ArrowLeft, CircleCheck, Loader, Github, Twitter, Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Toaster } from "@/components/ui/sonner";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const socialLinks = [{
  icon: Twitter,
  href: "#",
  label: "Twitter"
}, {
  icon: Linkedin,
  href: "#",
  label: "LinkedIn"
}, {
  icon: Github,
  href: "#",
  label: "GitHub"
}];
const budgetOptions = ["1 - 50 Units", "51 - 200 Units", "201 - 500 Units", "Bulk Order (500+)"];
function CollaborationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const nextStep = (e: React.MouseEvent) => {
    e.preventDefault();
    setStep(2);
  };
  const prevStep = (e: React.MouseEvent) => {
    e.preventDefault();
    setStep(1);
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-md bg-primary/10 flex items-center justify-center">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-semibold text-primary">Quotation Request Received</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        </div>
        <Button onClick={() => {
        resetForm();
        setStep(1);
      }} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
          New Quotation Request
        </Button>
      </div>;
  }
  const inputStyles = "border-0 border-b border-border rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-primary bg-transparent dark:bg-transparent transition-colors font-mono text-sm";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <div className="flex items-center gap-4 mb-8">
        <div className={`h-1 flex-1 rounded-md transition-colors duration-300 ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
        <div className={`h-1 flex-1 rounded-md transition-colors duration-300 ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
      </div>

      <div className={`space-y-6 transition-all duration-500 ${step === 1 ? 'block opacity-100' : 'hidden opacity-0'}`}>
        <div className="space-y-2 mb-6">
          <h3 className="text-xl font-default font-semibold text-primary uppercase tracking-tight">Step 1: Contact Information</h3>
          <p className="text-sm text-muted-foreground">Please provide your professional contact details.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="name" rules={{
          required: "Full Name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Full Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your name" className={inputStyles} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Email Address *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="email@company.com" className={inputStyles} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Company Name</FormLabel>
                <FormControl>
                  <Input placeholder="Marine Services Ltd." className={inputStyles} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          pattern: {
            value: /^[0-9+\-\s()]*$/,
            message: "Invalid phone format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+00 000 000 000" className={inputStyles} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="flex justify-end pt-4">
          <Button type="button" onClick={nextStep} className="group bg-primary hover:bg-secondary hover:text-primary text-primary-foreground rounded-md transition-all">
            Next: Part Details
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </div>

      <div className={`space-y-6 transition-all duration-500 ${step === 2 ? 'block opacity-100' : 'hidden opacity-0'}`}>
        <div className="space-y-2 mb-6">
          <h3 className="text-xl font-default font-semibold text-primary uppercase tracking-tight">Step 2: Part Specifications</h3>
          <p className="text-sm text-muted-foreground">Specify the marine spare parts and quantities required.</p>
        </div>

        <FormField control={control} name="project_details" rules={{
        required: "Part numbers are required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Part Numbers & Descriptions *</FormLabel>
              <FormControl>
                <Textarea placeholder="List OEM part numbers (e.g., 6E5-85570-10) and descriptions." className={`min-h-[120px] resize-none ${inputStyles}`} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="budget" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Required Quantity</FormLabel>
                <FormControl>
                  <select {...field} className={`w-full h-9 bg-transparent outline-none text-sm ${inputStyles} appearance-none cursor-pointer`}>
                    <option value="" disabled className="text-muted-foreground dark:bg-background">Select volume...</option>
                    {budgetOptions.map((option, i) => <option key={i} value={option} data-index={i} className="dark:bg-background text-foreground">
                        {option}
                      </option>)}
                  </select>
                </FormControl>
                <FormDescription className="text-[10px] uppercase font-mono">Select the estimated order volume.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="timeline" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Required By Date</FormLabel>
                <FormControl>
                  <Input type="date" className={inputStyles} {...field} />
                </FormControl>
                <FormDescription className="text-[10px] uppercase font-mono">Desired delivery timeline.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive font-medium">{submitError}</p>
          </div>}

        <div className="flex justify-between pt-4">
          <Button type="button" variant="ghost" onClick={prevStep} className="group text-muted-foreground hover:text-primary">
            <ArrowLeft className="mr-2 size-4 transition-transform group-hover:-translate-x-1" />
            Back
          </Button>
          <Button type="submit" disabled={isSubmitting} className="min-w-[180px] bg-primary hover:bg-secondary hover:text-primary text-primary-foreground rounded-md shadow-md transition-all">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Processing...
              </> : 'Submit Quotation'}
          </Button>
        </div>
      </div>
    </Form>;
}
export default function QuotationRequestForm() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="238034" id="quotation-request-form" className="bg-background py-24 lg:py-32 relative overflow-hidden" ref={sectionRef}>
      <Toaster position="bottom-right" />
      
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#22446608_1px,transparent_1px),linear-gradient(to_bottom,#22446608_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {}
          <div className={`lg:col-span-5 space-y-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div>
              <h2 className="text-4xl md:text-5xl font-default font-bold tracking-tighter text-primary mb-4 uppercase">
                Request a Quotation
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-default">
                Secure premium OEM-quality spare parts for your marine fleet. Our technical team provides precise quotes for Yamaha-compatible components and custom marine engineering solutions.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-md bg-primary/5 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors border border-primary/10">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold font-default text-foreground uppercase text-sm tracking-wider">Global Logistics Hub</h4>
                  <p className="text-muted-foreground mt-1 text-sm">Industrial Zone 4, Marine District<br />Tokyo, Japan / Global Distribution</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-md bg-primary/5 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors border border-primary/10">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold font-default text-foreground uppercase text-sm tracking-wider">Technical Support</h4>
                  <p className="text-muted-foreground mt-1 text-sm">
                    <Link to="mailto:sales@alttaimarine.com" className="hover:text-primary transition-colors text-foreground font-mono">
                      sales@alttaimarine.com
                    </Link>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 rounded-md bg-primary/5 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors border border-primary/10">
                  <Phone className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold font-default text-foreground uppercase text-sm tracking-wider">Direct Inquiry</h4>
                  <p className="text-muted-foreground mt-1 text-sm">
                    <Link to="tel:+8135550198" className="hover:text-primary transition-colors text-foreground font-mono">
                      +81 (3) 5550-1982
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <Separator className="bg-border/50" />

            <div className="space-y-4">
              <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">OEM Network</h4>
              <div className="flex gap-4">
                {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return <Link key={i} to={social.href} data-index={i} className="p-2 rounded-md bg-muted text-primary hover:bg-primary hover:text-primary-foreground transition-colors border border-border" aria-label={social.label}>
                      <Icon className="size-5" />
                    </Link>;
              })}
              </div>
            </div>

            {}
            <div className="relative aspect-[4/3] w-full rounded-md overflow-hidden border border-border bg-muted/30 group">
              <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686328/img/marine_logistics_map.png)] bg-cover bg-center opacity-30 grayscale group-hover:grayscale-0 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div className="flex items-center gap-2 bg-background/95 backdrop-blur-sm px-3 py-1.5 rounded-md border border-border shadow-sm">
                  <div className="size-2 rounded-lg bg-primary animate-pulse" />
                  <span className="text-[10px] font-mono font-bold tracking-tighter text-primary">ALTTAI_LOGISTICS_ACTIVE</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Card className="border-border shadow-xl shadow-primary/5 bg-card/80 backdrop-blur-sm h-full rounded-md">
              <CardContent className="p-8 md:p-10">
                <FormProvider formKey="quotation_form_request_quotation" formId="quotation_form_request_quotation" sectionName="quotation-request-form" defaultValues={{
                name: "",
                email: "",
                company: "",
                phone: "",
                project_details: "",
                budget: "",
                timeline: ""
              }} submitText="Submit Quotation" successMessage="Your technical request has been logged. An ALTTAI MARINE specialist will review your part specifications and contact you within 24 hours.">
                  <CollaborationForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}